/*
 * Golf Solution Calculator (issue #94). Build 2026-09-30b (whole-card tap fix).
 *
 * Reads the selected cage, mat and optional target from the section's
 * inputs, keeps the summary and total in sync, and adds the whole setup to
 * the cart in one /cart/add.js call, then sends the shopper to the cart.
 *
 * Every option is a checkbox so it can be unticked. Cage and mat carry
 * data-gsc-single: ticking one unticks the rest of that step. Only the cage
 * is required; mat and target are optional.
 *
 * Events go straight to GA4 (measurement ID from the section setting,
 * default G-LLHD4DV2M5). GA4's page tracking on this store runs inside the
 * Google & YouTube app pixel (no GTM, no page-level gtag), which can't see
 * custom events, so the calculator loads gtag.js itself the first time it
 * sends an event, with send_page_view off so page views are not counted
 * twice. It uses the same first-party _ga cookie, so events join the same
 * users and sessions. If Shopify's customer privacy API is present and
 * analytics is not allowed, nothing is sent.
 *
 * Every line added to cart carries the hidden property
 * _kit_source: "Golf Solution Calculator", so orders can be tagged by a
 * Shopify Flow keyed on the attribute KEY (same pattern as the Catnets DIY
 * builder, proven safe with the shared Cin7 / 3PL sync).
 *
 * GA4 event names:
 *   golf_calc_select       { gsc_step, gsc_item, gsc_qty, gsc_value }
 *   golf_calc_deselect     { gsc_step, gsc_item }
 *   golf_calc_add_to_cart  { currency, value, items[], gsc_value, gsc_items, gsc_cage, gsc_mat, gsc_target }
 *   golf_calc_error        { gsc_message }
 *   golf_calc_open         {}  (popup mode only)
 *   golf_calc_qty          { gsc_step, gsc_item, gsc_qty }  (-/+ on an add-on)
 */
(function () {
  'use strict';

  var STEPS = ['cage', 'mat', 'target'];
  var KIT_SOURCE = 'Golf Solution Calculator';

  function Calculator(root) {
    this.root = root;
    this.config = this.readConfig();
    this.money = this.moneyFormatter();

    this.linesEl = root.querySelector('[data-gsc-lines]');
    this.totalEl = root.querySelector('[data-gsc-total]');
    this.hintEl = root.querySelector('[data-gsc-hint]');
    this.buttonEl = root.querySelector('[data-gsc-add]');
    this.buttonLabelEl = root.querySelector('[data-gsc-add-label]');
    this.errorEl = root.querySelector('[data-gsc-error]');

    this.busy = false;
    this.bind();
    this.render();
  }

  Calculator.prototype.readConfig = function () {
    var el = this.root.querySelector('[data-gsc-config]');
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      return {};
    }
  };

  // Pinned to en-AU so a visitor browsing from overseas still sees "$799.00",
  // not "A$799.00" or "AUD 799.00".
  Calculator.prototype.moneyFormatter = function () {
    var currency = this.config.currency || 'AUD';
    try {
      var fmt = new Intl.NumberFormat(this.config.locale || 'en-AU', { style: 'currency', currency: currency });
      return function (cents) { return fmt.format(cents / 100); };
    } catch (err) {
      return function (cents) { return '$' + (cents / 100).toFixed(2); };
    }
  };

  Calculator.prototype.bind = function () {
    var self = this;

    this.root.addEventListener('change', function (event) {
      var input = event.target;
      if (!input || !input.hasAttribute('data-gsc-group')) return;
      var group = input.getAttribute('data-gsc-group');

      // One pick per single-choice step: ticking one unticks the others.
      if (input.checked && input.hasAttribute('data-gsc-single')) {
        var siblings = self.root.querySelectorAll('[data-gsc-group="' + group + '"]');
        for (var i = 0; i < siblings.length; i++) {
          if (siblings[i] !== input) siblings[i].checked = false;
        }
      }

      self.hideError();
      self.render();

      if (input.checked) {
        self.track('golf_calc_select', {
          gsc_step: group,
          gsc_item: input.getAttribute('data-gsc-title'),
          gsc_qty: self.qtyOf(input),
          gsc_value: (self.priceOf(input) * self.qtyOf(input)) / 100
        });
      } else {
        self.track('golf_calc_deselect', {
          gsc_step: group,
          gsc_item: input.getAttribute('data-gsc-title')
        });
      }
    });

    /*
     * The whole card toggles its option, handled here rather than by the
     * <label>. The theme loads hand.min.js, which cancels the click a label
     * forwards to its input after a touch, so on phones a tap highlighted
     * the card but never ticked it. Toggling the input ourselves sidesteps
     * that and makes the full card (not just the label) clickable.
     */
    this.root.addEventListener('click', function (event) {
      var wrap = event.target.closest && event.target.closest('.gsc__option-wrap');
      if (!wrap || !self.root.contains(wrap)) return;
      if (event.target.closest('a')) return; // "View details" keeps working

      // Quantity -/+ : change the amount; raising it also ticks the option.
      var stepBtn = event.target.closest('[data-gsc-qty-step]');
      if (stepBtn) {
        event.preventDefault();
        self.stepQty(wrap, parseInt(stepBtn.getAttribute('data-gsc-qty-step'), 10) || 0);
        return;
      }
      if (event.target.closest('[data-gsc-qty-control]')) return; // label/note area

      var input = wrap.querySelector('[data-gsc-group]');
      if (!input || input.disabled) return;

      // A click aimed at the input itself (keyboard space) toggles natively.
      if (event.target === input) return;

      event.preventDefault(); // stop the label also toggling it (double toggle)
      input.checked = !input.checked;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    if (this.buttonEl) {
      this.buttonEl.addEventListener('click', function () {
        self.addToCart();
      });
    }
  };

  Calculator.prototype.priceOf = function (input) {
    return parseInt(input.getAttribute('data-gsc-price'), 10) || 0;
  };

  Calculator.prototype.qtyOf = function (input) {
    return Math.max(1, parseInt(input.getAttribute('data-gsc-qty'), 10) || 1);
  };

  var MAX_QTY = 20;

  Calculator.prototype.stepQty = function (wrap, delta) {
    var input = wrap.querySelector('[data-gsc-group]');
    if (!input || input.disabled || !delta) return;
    var qty = Math.min(MAX_QTY, Math.max(1, this.qtyOf(input) + delta));
    input.setAttribute('data-gsc-qty', String(qty));
    var out = wrap.querySelector('[data-gsc-qty-value]');
    if (out) out.textContent = String(qty);

    if (delta > 0 && !input.checked) {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      this.render();
    }
    this.track('golf_calc_qty', {
      gsc_step: input.getAttribute('data-gsc-group'),
      gsc_item: input.getAttribute('data-gsc-title'),
      gsc_qty: qty
    });
  };

  // Selected, available inputs for one step. Disabled (sold-out) inputs
  // never count.
  Calculator.prototype.selected = function (group) {
    var inputs = this.root.querySelectorAll('[data-gsc-group="' + group + '"]:checked');
    var out = [];
    for (var i = 0; i < inputs.length; i++) {
      if (inputs[i].disabled) continue;
      out.push(inputs[i]);
    }
    return out;
  };

  Calculator.prototype.state = function () {
    var self = this;
    var picks = {};
    var lines = [];
    var total = 0;

    STEPS.forEach(function (step) {
      picks[step] = self.selected(step);
      picks[step].forEach(function (input) {
        var unit = self.priceOf(input);
        var qty = self.qtyOf(input);
        total += unit * qty;
        lines.push({
          step: step,
          id: parseInt(input.value, 10),
          title: input.getAttribute('data-gsc-title'),
          sku: input.getAttribute('data-gsc-sku'),
          unit: unit,
          qty: qty,
          price: unit * qty
        });
      });
    });

    var hasCage = picks.cage.length > 0;
    var hasMat = picks.mat.length > 0;

    return {
      picks: picks,
      lines: lines,
      total: total,
      hasCage: hasCage,
      hasMat: hasMat,
      ready: hasCage
    };
  };

  Calculator.prototype.render = function () {
    var s = this.state();
    var self = this;

    // Class-based checked state, so browsers without :has() still show it.
    var inputs = this.root.querySelectorAll('[data-gsc-group]');
    for (var i = 0; i < inputs.length; i++) {
      var card = inputs[i].closest('.gsc__option-wrap');
      if (card) card.classList.toggle('is-checked', inputs[i].checked);
    }

    // Mark each step done so the CSS can show progress.
    STEPS.forEach(function (step) {
      var el = self.root.querySelector('[data-gsc-step="' + step + '"]');
      if (!el) return;
      var done = step === 'cage' ? s.hasCage : step === 'mat' ? s.hasMat : s.picks.target.length > 0;
      el.classList.toggle('is-done', done);
    });

    this.linesEl.innerHTML = '';
    if (!s.lines.length) {
      var empty = document.createElement('li');
      empty.className = 'gsc__line gsc__line--placeholder';
      empty.textContent = this.config.emptyLabel || '';
      this.linesEl.appendChild(empty);
    } else {
      s.lines.forEach(function (line) {
        var li = document.createElement('li');
        li.className = 'gsc__line';
        var name = document.createElement('span');
        name.className = 'gsc__line-name';
        name.textContent = line.qty > 1 ? line.title + ' × ' + line.qty : line.title;
        var price = document.createElement('span');
        price.className = 'gsc__line-price';
        price.textContent = self.money(line.price);
        li.appendChild(name);
        li.appendChild(price);
        self.linesEl.appendChild(li);
      });
    }

    this.totalEl.textContent = this.money(s.total);

    var hint = !s.hasCage ? this.config.hintCage : !s.hasMat ? this.config.hintMat : this.config.hintReady;
    this.hintEl.textContent = hint || '';

    this.buttonEl.disabled = !s.ready || this.busy;
  };

  Calculator.prototype.addToCart = function () {
    var s = this.state();
    if (!s.ready || this.busy) return;

    var self = this;
    this.busy = true;
    this.buttonEl.disabled = true;
    this.buttonLabelEl.textContent = this.config.addingLabel || '...';
    this.hideError();

    var payload = {
      items: s.lines.map(function (line) {
        return { id: line.id, quantity: line.qty, properties: { _kit_source: KIT_SOURCE } };
      })
    };

    fetch(this.config.cartAddUrl || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (response) {
        if (response.ok) return response.json();
        // Shopify's 422 says exactly which item failed, so show that rather
        // than a generic message.
        return response.json().then(
          function (body) { throw new Error((body && (body.description || body.message)) || ''); },
          function () { throw new Error(''); }
        );
      })
      .then(function () {
        self.track('golf_calc_add_to_cart', {
          currency: self.config.currency || 'AUD',
          value: s.total / 100,
          items: s.lines.map(function (line) {
            return { item_id: line.sku || String(line.id), item_name: line.title, item_category: line.step, price: line.unit / 100, quantity: line.qty };
          }),
          gsc_value: s.total / 100,
          gsc_items: s.lines.length,
          gsc_cage: titles(s.picks.cage),
          gsc_mat: s.picks.mat.length ? titles(s.picks.mat) : 'none',
          gsc_target: s.picks.target.length ? titles(s.picks.target) : 'none'
        }, function () {
          window.location.href = self.config.cartUrl || '/cart';
        });
      })
      .catch(function (err) {
        var message = (err && err.message) || self.config.errorLabel;
        self.showError(message);
        self.track('golf_calc_error', { gsc_message: message });
        self.busy = false;
        self.buttonLabelEl.textContent = self.config.buttonLabel;
        self.render();
      });
  };

  Calculator.prototype.showError = function (message) {
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  };

  Calculator.prototype.hideError = function () {
    this.errorEl.hidden = true;
    this.errorEl.textContent = '';
  };

  // Tracking must never block the build or the cart.
  Calculator.prototype.track = function (event, data, done) {
    var params = { gsc_page: window.location.pathname };
    for (var key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) params[key] = data[key];
    }
    // Selection events carry a price: report it as a GA4 value.
    if (params.value === undefined && typeof params.gsc_value === 'number') {
      params.value = params.gsc_value;
      params.currency = this.config.currency || 'AUD';
    }
    sendToGa4(this.config.ga4Id, event, params, done);
  };

  function analyticsAllowed() {
    var cp = window.Shopify && window.Shopify.customerPrivacy;
    if (cp && typeof cp.analyticsProcessingAllowed === 'function') {
      return cp.analyticsProcessingAllowed() !== false;
    }
    return true;
  }

  // Load gtag.js once for this measurement ID, without a page view.
  function ensureGtag(id) {
    window.__gscGtag = window.__gscGtag || {};
    if (window.__gscGtag[id]) return;
    window.__gscGtag[id] = true;

    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag !== 'function') {
      window.gtag = function () { window.dataLayer.push(arguments); };
      window.gtag('js', new Date());
    }
    if (!document.querySelector('script[src*="googletagmanager.com/gtag/js?id=' + id + '"]')) {
      var tag = document.createElement('script');
      tag.async = true;
      tag.src = 'https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(id);
      document.head.appendChild(tag);
    }
    window.gtag('config', id, { send_page_view: false });
  }

  // `done` (optional) runs once GA4 has taken the hit, or after 1s at most,
  // so a redirect straight after an event doesn't drop it.
  function sendToGa4(id, event, params, done) {
    var called = false;
    function finish() {
      if (called || typeof done !== 'function') return;
      called = true;
      done();
    }

    if (!id) return finish();
    try {
      if (!analyticsAllowed()) return finish();
      ensureGtag(id);
      var payload = { send_to: id, event_callback: finish, event_timeout: 1000 };
      for (var k in params) payload[k] = params[k];
      window.gtag('event', event, payload);
      setTimeout(finish, 1000);
    } catch (err) {
      finish(); // tracking must never break the calculator
    }
  }

  function titles(inputs) {
    return inputs.map(function (i) { return i.getAttribute('data-gsc-title'); }).join(' | ');
  }

  /*
   * Popup mode. Any [data-gsc-open] element opens the calculator dialog: the
   * section's own button or the launcher snippet in the product buy box.
   * data-gsc-open="<section id>" targets one section; empty opens the first.
   * Bound once at document level so a launcher rendered anywhere works.
   */
  function findDialog(target) {
    if (target) {
      var root = document.getElementById('gsc-' + target);
      if (root) return root.querySelector('[data-gsc-dialog]');
    }
    return document.querySelector('[data-gsc] [data-gsc-dialog]');
  }

  function openDialog(dialog) {
    if (!dialog || dialog.open) return;
    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
    document.documentElement.classList.add('gsc-lock');
    var root = dialog.closest('[data-gsc]');
    if (root && root.__gsc) root.__gsc.track('golf_calc_open', {});
  }

  function closeDialog(dialog) {
    if (!dialog) return;
    if (typeof dialog.close === 'function' && dialog.open) {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
    }
    document.documentElement.classList.remove('gsc-lock');
  }

  function bindDialogs() {
    if (document.__gscDialogsBound) return;
    document.__gscDialogsBound = true;

    document.addEventListener('click', function (event) {
      var opener = event.target.closest && event.target.closest('[data-gsc-open]');
      if (opener) {
        event.preventDefault();
        openDialog(findDialog(opener.getAttribute('data-gsc-open')));
        return;
      }

      var closer = event.target.closest && event.target.closest('[data-gsc-close]');
      if (closer) {
        closeDialog(closer.closest('[data-gsc-dialog]'));
        return;
      }

      // A click on the backdrop lands on the dialog element itself.
      if (event.target.hasAttribute && event.target.hasAttribute('data-gsc-dialog')) {
        closeDialog(event.target);
      }
    });

    // Escape closes a modal dialog natively; keep the scroll lock in step.
    document.addEventListener('close', function (event) {
      if (event.target.hasAttribute && event.target.hasAttribute('data-gsc-dialog')) {
        document.documentElement.classList.remove('gsc-lock');
      }
    }, true);
  }

  /*
   * The Gorgias chat bubble sits bottom-right and, on phones, covered the
   * Add button in the inline calculator's sticky bar. While an inline
   * calculator is on screen on mobile, lift the bubble above the bar with
   * Gorgias's own setPosition API, and put it back (0/0) when it scrolls
   * away. Popup mode doesn't need this: the dialog sits above the bubble.
   */
  function liftChatOverBar(root) {
    if (root.__gscChat || root.classList.contains('gsc--button-mode')) return;
    if (!('IntersectionObserver' in window) || !window.matchMedia) return;
    root.__gscChat = true;

    var mobile = window.matchMedia('(max-width: 989px)');
    var BUBBLE_ZONE = 90; // default bubble: ~54px tall, ~20px off the bottom
    var visible = false;
    var current = null;
    var queued = false;

    // Where the bubble must sit so it clears the bar. The bar is pinned to
    // the bottom while you scroll through the steps, then rides up with the
    // page at the end of the calculator, so this is measured, not fixed.
    function wantedOffset() {
      if (!visible || !mobile.matches) return 0;
      var bar = root.querySelector('.gsc__summary-inner');
      if (!bar) return 0;
      var r = bar.getBoundingClientRect();
      var vh = window.innerHeight;
      if (r.bottom < vh - BUBBLE_ZONE || r.top > vh) return 0; // bar clear of the bubble
      return Math.max(0, Math.round(vh - r.top + 8));
    }

    function place() {
      queued = false;
      var chat = window.GorgiasChat;
      if (!chat || typeof chat.setPosition !== 'function') return;
      var offsetY = wantedOffset();
      if (current !== null && Math.abs(offsetY - current) < 6) return;
      try {
        chat.setPosition({ alignment: 'bottom-right', offsetX: 0, offsetY: offsetY });
        current = offsetY;
      } catch (err) { /* chat positioning is cosmetic */ }
    }

    function queue() {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(place);
    }

    new IntersectionObserver(function (entries) {
      visible = entries[0].isIntersecting;
      queue();
    }).observe(root);

    window.addEventListener('scroll', function () { if (visible) queue(); }, { passive: true });
    window.addEventListener('resize', queue);
    if (mobile.addEventListener) mobile.addEventListener('change', queue);

    // Gorgias loads after the theme; place once its API is ready.
    var tries = 0;
    var timer = setInterval(function () {
      tries += 1;
      var chat = window.GorgiasChat;
      if ((chat && chat.setPosition) || tries > 40) {
        clearInterval(timer);
        current = null;
        if (chat && typeof chat.init === 'function') {
          try { chat.init().then(function () { current = null; queue(); }); } catch (err) { /* ignore */ }
        }
        queue();
      }
    }, 500);
  }

  function init() {
    bindDialogs();
    var roots = document.querySelectorAll('[data-gsc]');
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].__gsc) continue;
      roots[i].__gsc = new Calculator(roots[i]);
      liftChatOverBar(roots[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialise when the section is added or re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', init);
})();
